import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const ROLES = [
  {
    value: "super_admin",
    label: "Super Admin",
    color: "#a78bfa",
    icon: "👑",
    description: "Full access to everything. Can create, edit, and deactivate any user. Can assign all roles including Super Admin. Bypasses all permission checks.",
    capabilities: ["Manage all users", "Assign any role", "Full section access", "Configure permissions"],
  },
  {
    value: "admin",
    label: "Admin",
    color: "#60a5fa",
    icon: "🛡️",
    description: "Can manage users except Super Admins. Can assign roles below Admin level. Full access to all dashboard sections.",
    capabilities: ["Manage editors and viewers", "Assign Editor/Viewer roles", "Full section access", "Reset passwords"],
  },
  {
    value: "editor",
    label: "Editor",
    color: "#34d399",
    icon: "✏️",
    description: "Can view all dashboard sections. Cannot manage users or change any settings.",
    capabilities: ["View all sections", "Export data", "Cannot manage users"],
  },
  {
    value: "viewer",
    label: "Viewer",
    color: "#94a3b8",
    icon: "👁️",
    description: "Can only view sections they are explicitly granted access to. No access by default until a Super Admin or Admin grants specific sections.",
    capabilities: ["Access granted sections only", "No user management", "Cannot export data"],
  },
];

export function RolesPage({ theme: t }) {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithAuth(`${API}/api/admin/users`);
        if (!res.ok) return;
        const users = await res.json();
        const c = {};
        for (const u of users) c[u.role] = (c[u.role] || 0) + 1;
        setCounts(c);
      } catch {}
    })();
  }, []);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>Role Management</h3>
        <p style={{ margin: "4px 0 0", fontSize: 12, color: t.textMuted }}>Built-in roles cannot be renamed or deleted.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
        {ROLES.map(role => (
          <div key={role.value} style={{ background: t.surface, border: `1px solid ${role.color}33`, borderRadius: 16, padding: 20, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: role.color }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 22 }}>{role.icon}</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>{role.label}</div>
                  <div style={{ fontSize: 10, color: role.color, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8 }}>{role.value}</div>
                </div>
              </div>
              <div style={{ background: `${role.color}18`, border: `1px solid ${role.color}33`, borderRadius: 20, padding: "4px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 16, fontWeight: 900, color: role.color }}>{counts[role.value] || 0}</div>
                <div style={{ fontSize: 9, color: t.textFaint, textTransform: "uppercase" }}>users</div>
              </div>
            </div>

            <p style={{ fontSize: 12, color: t.textSub, lineHeight: 1.6, marginBottom: 14 }}>{role.description}</p>

            <div>
              <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginBottom: 8 }}>Capabilities</div>
              {role.capabilities.map(cap => (
                <div key={cap} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: cap.startsWith("Cannot") ? "#DC2626" : role.color }}>
                    {cap.startsWith("Cannot") ? "✗" : "✓"}
                  </span>
                  <span style={{ fontSize: 11, color: cap.startsWith("Cannot") ? t.textMuted : t.textSub }}>{cap}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 10, background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 6, padding: "2px 8px", color: t.textFaint }}>Built-in · Cannot delete</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
